/**
 * Push subscription.
 *
 * Permission is never requested on page load. A cold prompt gets denied
 * and browsers make denial sticky -- once blocked, the user has to dig
 * into site settings to undo it, and most never will. So the prompt
 * only fires from a deliberate click.
 */
const urlBase64ToUint8Array = (base64: string): Uint8Array<ArrayBuffer> => {
  const padded = base64.padEnd(
    base64.length + ((4 - (base64.length % 4)) % 4),
    '=',
  );
  const raw = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));

  // Must be backed by a plain ArrayBuffer: applicationServerKey rejects
  // a view over SharedArrayBuffer, which the bare type permits.
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
};

export type PushState =
  | 'unsupported'
  | 'needs-install'
  | 'default'
  | 'granted'
  | 'denied'
  | 'shell';

import { needsIosInstall } from './install.ts';
import { deviceToken } from './api.ts';
import { authHeaders } from './api.ts';

export const pushState = (): PushState => {
  // The desktop shell fires native Windows toasts, so the WebView's
  // own Notification API is irrelevant. Reporting it as blocked told
  // a counter screen its alerts were off while they were arriving.
  if (deviceToken()) return 'shell';

  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return 'unsupported';
  }
  // On iOS a tab can never receive push, whatever Notification says.
  if (needsIosInstall()) return 'needs-install';
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission as PushState;
};

/** True once a subscription exists on this device for this origin. */
export const isSubscribed = async (): Promise<boolean> => {
  if (!('serviceWorker' in navigator)) return false;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  return Boolean(sub);
};

export type EnableResult = 'enabled' | 'denied' | 'failed';

/**
 * Request permission and register with the server. Call only from a
 * click handler.
 */
export const enablePush = async (): Promise<EnableResult> => {
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return 'denied';

    const reg = await navigator.serviceWorker.ready;
    const { publicKey } = await fetch('/push/key', {
      credentials: 'same-origin',
      headers: authHeaders(),
    }).then((r) => r.json());

    const existing = await reg.pushManager.getSubscription();
    const sub =
      existing ??
      (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      }));

    const json = sub.toJSON() as {
      endpoint?: string;
      keys?: { p256dh?: string; auth?: string };
    };
    if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) {
      return 'failed';
    }

    const response = await fetch('/push/subscribe', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ endpoint: json.endpoint, keys: json.keys }),
    });

    return response.ok ? 'enabled' : 'failed';
  } catch {
    return 'failed';
  }
};

/**
 * Unsubscribe locally and on the server. Both matter -- leaving the row
 * behind means the server keeps pushing to an endpoint the browser has
 * stopped honouring.
 */
export const disablePush = async (): Promise<void> => {
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return;

  await fetch('/push/unsubscribe', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ endpoint: sub.endpoint }),
  }).catch(() => undefined);

  await sub.unsubscribe().catch(() => undefined);
};
